import COPY from "./content/siteCopy.json";
import React, { useEffect, useRef, useState } from "react";
import DOMPurify from "dompurify";
import { ChevronLeft, ChevronRight, Heart, MessageCircle, Send, Bookmark, Instagram, X, Link2, Check } from "lucide-react";
import { CalendlyLink } from "./SiteHeader";

const IG_URL = "https://www.instagram.com/hellomortgage.ca/";
const IG_KEY = "hm_ig_invite_done";
const INVITE = {
  heart: ["Thanks for the love.", "There's a whole feed of this on Instagram: mortgage myths, Alberta market takes, the odd bad pun. Come hang out."],
  save: ["Saved. Smart.", "The rest of them live on Instagram, one post at a time, zero mortgage-speak. Worth a follow if this one helped."],
};
const BURST = Array.from({ length: 10 }, (_, i) => ({ a: i * 36, c: i % 3 ? "var(--pink)" : "var(--navy)" }));

function HeartBurst() {
  return <span className="sp-burst" aria-hidden="true">{BURST.map((b, i) => <i key={i} style={{ "--a": `${b.a}deg`, background: b.c }} />)}</span>;
}

function useInvite() {
  const [kind, setKind] = useState(null);
  const timer = useRef(null);
  const show = (k) => {
    if (window.sessionStorage.getItem(IG_KEY)) return;
    if (Date.now() < Number(window.localStorage.getItem(IG_KEY) || 0)) return;
    setKind(k);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setKind(null), 12000);
  };
  // followed: quiet for 30 days; "maybe later"/dismiss: quiet for this tab session only
  const close = (how) => {
    clearTimeout(timer.current); setKind(null);
    if (how === "followed") window.localStorage.setItem(IG_KEY, String(Date.now() + 30 * 864e5));
    else if (how === "later") window.sessionStorage.setItem(IG_KEY, "1");
  };
  useEffect(() => () => clearTimeout(timer.current), []);
  return [kind, show, close];
}

function InviteCard({ kind, onFollow, onLater }) {
  const [head, body] = INVITE[kind];
  return (
    <div className="sp-invite" role="status" data-testid="solution-carousel-invite">
      <img src="/brand/social-avatar.png" alt="" className="sp-invite-avatar" />
      <div className="sp-invite-text"><b>{head}</b><span>{body}</span></div>
      <div className="sp-invite-actions">
        <a href={IG_URL} target="_blank" rel="noopener" className="sp-invite-follow" onClick={onFollow} data-testid="solution-carousel-follow"><Instagram size={16} /> Follow @hellomortgage.ca</a>
        <button type="button" className="sp-invite-later" onClick={onLater} data-testid="solution-carousel-later">Maybe later</button>
      </div>
      <button type="button" className="sp-invite-x" onClick={onLater} aria-label="Dismiss" data-testid="solution-carousel-invite-close"><X size={14} /></button>
    </div>
  );
}

function ShareMenu({ title, onClose }) {
  const [copied, setCopied] = useState(false);
  const url = typeof window !== "undefined" ? window.location.href.split("#")[0] : "";
  const text = `${title} — from hellomortgage.ca`;
  const enc = encodeURIComponent;
  const copy = async () => { try { await navigator.clipboard.writeText(url); setCopied(true); setTimeout(onClose, 1200); } catch { setCopied(false); } };
  return (
    <div className="sp-share" role="menu" data-testid="solution-carousel-share-menu">
      <span className="sp-share-k">Share this page</span>
      <a href={`https://www.facebook.com/sharer/sharer.php?u=${enc(url)}&quote=${enc(text)}`} target="_blank" rel="noopener" onClick={onClose} data-testid="solution-carousel-share-facebook">Facebook</a>
      <a href={`https://twitter.com/intent/tweet?text=${enc(text)}&url=${enc(url)}`} target="_blank" rel="noopener" onClick={onClose} data-testid="solution-carousel-share-x">X</a>
      <a href={`https://www.linkedin.com/sharing/share-offsite/?url=${enc(url)}`} target="_blank" rel="noopener" onClick={onClose} data-testid="solution-carousel-share-linkedin">LinkedIn</a>
      <button type="button" onClick={copy} data-testid="solution-carousel-share-copy">{copied ? <><Check size={14} /> Copied</> : <><Link2 size={14} /> Copy link</>}</button>
    </div>
  );
}


// Shared like / comment / share / save row + follow-invite loop (used by the chapter carousel and the social post sections).
export function usePostSocial(title) {
  const [liked, setLiked] = useState(false);
  const [saved, setSaved] = useState(false);
  const [followed, setFollowed] = useState(false);
  const [burst, setBurst] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [invite, showInvite, closeInvite] = useInvite();
  const like = () => { const next = !liked; setLiked(next); if (next) { setBurst(true); setTimeout(() => setBurst(false), 900); showInvite("heart"); } };
  const save = () => { setSaved(true); showInvite("save"); };
  const share = async () => {
    const url = window.location.href.split("#")[0];
    if (navigator.share) { try { await navigator.share({ title: document.title, text: `${title} — from hellomortgage.ca`, url }); } catch {} return; }
    setShareOpen((o) => !o);
  };
  const onFollow = () => { setFollowed(true); setLiked(true); closeInvite("followed"); };
  return { liked, saved, followed, burst, shareOpen, invite, like, save, share, closeShare: () => setShareOpen(false), onFollow, onLater: () => closeInvite("later") };
}

export function PostActions({ s, title, center = null, below = null, testId = "post" }) {
  return (
    <>
      <div className="sp-actions" data-testid={`${testId}-actions`}>
        <button type="button" className={`sp-act sp-heart${s.liked ? " on" : ""}`} onClick={s.like} aria-label="Like" aria-pressed={s.liked} data-testid={`${testId}-like`}><Heart size={22} fill={s.liked ? "currentColor" : "none"} />{s.burst && <HeartBurst />}</button>
        <CalendlyLink className="sp-act" testId={`${testId}-comment`}><MessageCircle size={22} /><span className="sr-only">Ask us a question</span></CalendlyLink>
        <span className="sp-share-wrap">
          <button type="button" className={`sp-act${s.shareOpen ? " on" : ""}`} onClick={s.share} aria-label="Share this page" data-testid={`${testId}-share`}><Send size={22} /></button>
          {s.shareOpen && <ShareMenu title={title} onClose={s.closeShare} />}
        </span>
        {center}
        <button type="button" className={`sp-act${s.saved ? " on" : ""}`} onClick={s.save} aria-label="Save" aria-pressed={s.saved} data-testid={`${testId}-bookmark`}><Bookmark size={22} fill={s.saved ? "currentColor" : "none"} /></button>
      </div>
      {below}
      {s.invite && <InviteCard kind={s.invite} onFollow={s.onFollow} onLater={s.onLater} />}
    </>
  );
}

const clean = (h) => ({ __html: DOMPurify.sanitize(h || "", { FORBID_TAGS: ["script", "style", "iframe"] }) });
const pad = (n) => String(n).padStart(2, "0");
const HANDLE = "hellomortgage.ca";
const Y = [330, 80, 300, 110, 320, 70, 290, 120, 310, 90];
const tag = (s) => `#${(s || "").replace(/[^A-Za-z0-9 ]/g, "").split(/\s+/).map((w) => w[0]?.toUpperCase() + w.slice(1)).join("")}`;

// Dashed coral path that enters at Y[k] and exits at Y[k+1], so it reads as one line running through every slide.
function Path({ k, loop }) {
  const a = Y[k % Y.length], b = Y[(k + 1) % Y.length], m = (a + b) / 2;
  const d = loop
    ? `M -20 ${a} C 160 ${a}, 260 ${m}, 360 ${m} c 40 0 70 -45 40 -70 c -30 -25 -75 20 -40 55 C 420 ${m + 40}, 560 ${b}, 740 ${b}`
    : `M -20 ${a} C 220 ${a}, 300 ${b}, 420 ${m} S 600 ${b}, 740 ${b}`;
  return <svg className="sp-path" viewBox="0 0 720 400" preserveAspectRatio="none" aria-hidden="true"><path d={d} /></svg>;
}

function Frame({ k, total, label, children, cls = "" }) {
  return (
    <section className={`sp-slide ${cls}`} data-testid="solution-program-card">
      <span className="sp-tag">{tag(label)}</span>
      {children}
      <span className="sp-meta"><b>{HANDLE}</b><i>{pad(k + 1)} / {pad(total)}</i></span>
    </section>
  );
}

// Slide background treatments. "classic" = dashed coral path + pink block; the rest are alternates so the site doesn't repeat one look.
const DECOR = {
  classic: (k) => <Path k={k} loop={k === 0} />,
  ghost: (k) => <><span className="sp-ghost" aria-hidden="true">{pad(k + 1)}</span><span className="sp-halftone" aria-hidden="true" /></>,
  note: () => <><i className="sp-tape t1" aria-hidden="true" /><i className="sp-tape t2" aria-hidden="true" /></>,
  ink: () => <span className="sp-blob" aria-hidden="true" />,
  polaroid: (k) => <span className="sp-stamp" aria-hidden="true">{pad(k + 1)}</span>,
};
const slideCls = (variant, k) => (variant === "classic" ? (k % 2 ? "sp-b" : `sp-a${k > 0 ? " after-b" : ""}`) : `sp-v-${variant}${variant === "ink" && k % 2 ? " sp-dark" : ""}`);

function Art({ variant, icon, caption }) {
  if (!icon) return null;
  return (
    <div className="sp-art">
      <img className="sp-icon" src={icon} alt="" loading="lazy" />
      {variant === "polaroid" && <small className="sp-polaroid-cap">{caption}</small>}
    </div>
  );
}

// Chapter cards rendered as a social carousel post: one slide per card (alternating styles) → CTA slide.
export function ChapterCarousel({ cards, title, label, n, variant = "classic" }) {
  const track = useRef(null);
  const [i, setI] = useState(0);
  const s = usePostSocial(title);
  const { liked, saved, followed, save } = s;
  const total = cards.length + 1;
  const caption = followed ? "Following. You're officially our favourite." : liked || saved ? "Saved. Smart. Now go tell someone who's still guessing." : `Swipe through all ${cards.length}. Then tap the heart if this made it feel less scary.`;
  const go = (k) => { const next = Math.max(0, Math.min(total - 1, k)); const el = track.current; if (el) el.scrollTo({ left: next * el.clientWidth, behavior: "smooth" }); setI(next); };
  const onScroll = () => { const el = track.current; if (el) setI(Math.round(el.scrollLeft / el.clientWidth)); };
  return (
    <article className={`sp sp-var-${variant}`} data-testid={`solution-carousel-${n}`} data-variant={variant}>
      <header className="sp-head">
        <img src="/brand/social-avatar.png" alt="Hello Mortgage" className="sp-avatar" />
        <div><b>{HANDLE}</b><span>{title}</span></div>
        <span className="sp-count" data-testid="solution-carousel-count">{i + 1}/{total}</span>
      </header>
      <div className="sp-stage">
        <div className="sp-track" ref={track} onScroll={onScroll} data-testid="solution-carousel-track">
          {cards.map((c, k) => (
            <Frame k={k} total={total} label={label} cls={slideCls(variant, k)} key={c.title}>
              {DECOR[variant](k)}
              <div className="sp-body">
                <span className="num">{pad(k + 1)}</span><h3><span>{c.title}</span></h3>
                {c.texts.map((t, j) => <p key={j} dangerouslySetInnerHTML={clean(t)} />)}
                {c.steps.length > 0 && <ol className="chapter-card-steps">{c.steps.map((st, j) => <li key={j}>{st.title && <strong>{st.title}</strong>}<span dangerouslySetInnerHTML={clean(st.text)} /></li>)}</ol>}
                {c.bullets.length > 0 && <ul>{c.bullets.map((b, j) => <li key={j} dangerouslySetInnerHTML={clean(b)} />)}</ul>}
              </div>
              <Art variant={variant} icon={c.icon} caption={label} />
            </Frame>
          ))}
          <Frame k={total - 1} total={total} label={label} cls="sp-end">
            <Path k={total - 1} />
            <span className="sp-end-blob" aria-hidden="true" style={{ backgroundImage: "url(/brand/hello-languages-bg.jpg)" }} />
            <div className="sp-end-text">
              <span className="sp-end-kicker">That's all.</span>
              <h3 className="sp-end-title">Still with us?<em>Let's talk.</em></h3>
              <p>{COPY.solution.chapter_end.text.split(". Knowing")[0]}.</p>
              <p>Knowing{COPY.solution.chapter_end.text.split(". Knowing")[1]}</p>
              <div className="sp-end-actions">
                <CalendlyLink className="btn" testId={`solution-carousel-${n}-cta`}>Let's Get This Going</CalendlyLink>
                <button type="button" className={`sp-end-save${saved ? " on" : ""}`} onClick={save} data-testid={`solution-carousel-${n}-save`}><Bookmark size={16} fill={saved ? "currentColor" : "none"} />{saved ? "Saved" : "Save this post"}</button>
              </div>
            </div>
            <img src="/brand/mascot-call-me.png" alt="" className="sp-end-mascot" loading="lazy" />
          </Frame>
        </div>
        {i > 0 && <button type="button" className="sp-arrow prev" onClick={() => go(i - 1)} aria-label="Previous" data-testid="solution-carousel-prev"><ChevronLeft size={20} /></button>}
        {i < total - 1 && <button type="button" className="sp-arrow next" onClick={() => go(i + 1)} aria-label="Next" data-testid="solution-carousel-next"><ChevronRight size={20} /></button>}
      </div>
      <div className="sp-foot">
        <PostActions s={s} title={title} testId="solution-carousel" center={<span className="sp-dots" data-testid="solution-carousel-dots">{Array.from({ length: total }, (_, k) => <button type="button" key={k} className={k === i ? "on" : ""} onClick={() => go(k)} aria-label={`Slide ${k + 1}`} />)}</span>} />
        <p className="sp-caption" data-testid="solution-carousel-caption"><b>{HANDLE}</b> {caption}</p>
      </div>
    </article>
  );
}
